import { Table, tokPx } from '@sunwebgroup/ui-components'
import { Skeleton } from '@sunwebgroup/ui-components/uds'
import { eachDayOfInterval, format, isBefore, lastDayOfMonth, parseISO } from 'date-fns'
import { isEmpty } from 'ramda'
import { use, useCallback, useEffect, useMemo } from 'react'
import * as React from 'react'
import { css, styled } from 'styled-components'
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react'
import { Day } from '@/scopes/prices/components/PriceTable/Day'
import { NavigationByWeeksContext } from '@/scopes/prices/components/PriceTable/NavigationByWeeksContext'
import { PriceCell } from '@/scopes/prices/components/PriceTable/PriceCell'
import {
  getPriceRangeSelector,
  isPriceTableLoadingStateSelector,
  pricesResponseSelector,
  selectedPriceIdSelector,
} from '@/scopes/prices/components/selectors'
import { PricesActionsContext } from '@/scopes/prices/domain/PricesActionsContext'
import { usePricesSelector } from '@/scopes/prices/infrastructure/usePricesSelector'
import 'swiper/css'
import 'swiper'

const PriceTableRoot = styled(Table.Root)`
  width: 100%;
`

const DayHeader = styled(Table.Header)<{ $isHighlighted: boolean, $isSelected: boolean }>`
  padding: 0 ${tokPx('primitives_space_12')} 0 ${tokPx('primitives_space_12')};
  height: 66px;
  background: white;
  border: none;
  width: 100%;

  &:before {
    width: unset;
  }
`

const PriceTableColumn = styled(Table.Col)<{ $disabled?: boolean }>`
  margin-right: ${tokPx('primitives_space_6')};
  padding: 2.5px ${tokPx('primitives_space_6')};
  background: white;
  border-radius: ${tokPx('primitives_corner_radius_m')};
  width: 100%;
  position: relative;
  ${({ $disabled }) =>
    $disabled
    && css`
      opacity: 0.38;
    `}
`

const ColumnSkeleton = styled(Skeleton)`
  width: 100%;
  height: 360px;
  margin: 2.5px 6px;
`

export function GridMobile() {
  const { dateFrom, dateTo, minDatePrices } = usePricesSelector(getPriceRangeSelector)
  const { prices, pricesByDurationAndDateByWeeks } = usePricesSelector(pricesResponseSelector)
  const {
    focusedDate,
    navigateRight,
    navigateLeft,
    updateFocusedDate,
    setSwiper,
    swiperInstance: swiper,
    focusedSlide,
    setFocusedSlide,
    datesToDisplay,
    setDatesToDisplay,
    numberOfColumns,
    isNavigationLeftEnabled,
    isNavigationRightEnabled,
  } = React.use(NavigationByWeeksContext)

  const selectedPriceId = usePricesSelector(selectedPriceIdSelector)
  const isLoading = usePricesSelector(isPriceTableLoadingStateSelector)
  const { selectPrice } = use(PricesActionsContext)
  const selectedPrice = useMemo(() => prices?.find(price => price.id === selectedPriceId), [prices, selectedPriceId])

  const onPriceSelect = useCallback(
    (newSelectedPriceId: string) => {
      selectPrice(newSelectedPriceId)
    },
    [selectPrice],
  )

  useEffect(() => {
    swiper?.slideTo(focusedSlide, 300, false)
  }, [focusedSlide, swiper])

  useEffect(() => {
    if (!dateFrom || !dateTo || !prices || isEmpty(prices) || !focusedDate || !minDatePrices) {
      return
    }

    const minDatePricesMonth = format(focusedDate, 'yyyy-MM')
    const startDate = format(parseISO(minDatePricesMonth), 'yyyy-MM-01')
    const endDate = lastDayOfMonth(parseISO(minDatePricesMonth))

    const newDates = eachDayOfInterval({ start: parseISO(startDate), end: endDate }).map(date =>
      format(date, 'yyyy-MM-dd'),
    )

    setDatesToDisplay(newDates)
    setFocusedSlide(newDates.indexOf(format(focusedDate, 'yyyy-MM-dd')))
  }, [dateFrom, dateTo, prices, focusedDate])

  if (!dateFrom || !dateTo || !prices || isEmpty(prices) || !focusedDate) {
    return (
      <PriceTableRoot data-qa="price-table">
        {Array.from({ length: numberOfColumns }, (_, i) => (
          <ColumnSkeleton key={i} />
        ))}
      </PriceTableRoot>
    )
  }

  const today = format(new Date(), 'yyyy-MM-dd')
  const disableColumn = (date: string) => isBefore(parseISO(date), parseISO(today))

  const onSlideChangeHandler = (swiperInstance: SwiperClass) => {
    if (swiperInstance.isBeginning) {
      if (!isNavigationLeftEnabled)
        return
      navigateLeft(parseISO(datesToDisplay[0]))
    }
    else if (swiperInstance.isEnd) {
      if (!isNavigationRightEnabled)
        return
      navigateRight(parseISO(datesToDisplay[datesToDisplay.length - 1]))
    }
    else {
      updateFocusedDate(parseISO(datesToDisplay[swiperInstance.activeIndex]))
    }
  }

  return (
    <PriceTableRoot data-qa="price-table">
      <Swiper
        onSwiper={setSwiper}
        initialSlide={focusedSlide}
        onSlideChangeTransitionEnd={onSlideChangeHandler}
        centeredSlides
        spaceBetween={6}
        navigation
        slidesPerView={3.2}
        pagination={{ clickable: true }}
        scrollbar={{ draggable: true }}
        role="grid"
      >
        {datesToDisplay.map(date => (
          <SwiperSlide key={date}>
            {isLoading
              ? <ColumnSkeleton />
              : (
                  <PriceTableColumn key={date} $disabled={disableColumn(date)} role="row">
                    <DayHeader $isHighlighted={false} $isSelected={date === selectedPrice?.departureDate} role="columnheader">
                      <Day date={date} />
                    </DayHeader>
                    {!disableColumn(date)
                    && Object.keys(pricesByDurationAndDateByWeeks!).map((duration: string) => (
                      <PriceCell
                        key={duration + date}
                        price={pricesByDurationAndDateByWeeks![Number(duration)]?.[date]}
                        onClick={onPriceSelect}
                        isSelected={
                          selectedPrice?.departureDate === date && selectedPrice?.duration.toString() === duration
                        }
                      />
                    ))}
                  </PriceTableColumn>
                )}
          </SwiperSlide>
        ))}
      </Swiper>
    </PriceTableRoot>
  )
}
